import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw, Settings, Menu } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAppStore } from '../store';
import { useTranslation } from '../utils/translations';
import { NotificationCenter } from './NotificationCenter';

export default function Header() {
  const { t } = useTranslation();
  const { mt5Connected, toggleSidebar } = useAppStore();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header className="relative border-b border-slate-800/60 backdrop-blur-md bg-slate-900/70 sticky top-0 z-50 shadow-soft">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center gap-3 sm:gap-5">
            {/* Mobile menu button */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2.5 hover:bg-slate-800/60 rounded-lg transition-smooth active:scale-95"
            >
              <Menu className="w-5 h-5 text-slate-300" />
            </button>

            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg overflow-hidden ring-1 ring-white/10 shadow-sm shrink-0">
                <img src="/logo.png" alt="" className="h-full w-full object-cover" width={40} height={40} />
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-violet-400 via-violet-300 to-cyan-400 bg-clip-text text-transparent tracking-tight">
                TradeAnalyzer Pro
              </span>
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-7 w-px bg-gradient-to-b from-transparent via-slate-700 to-transparent" />

            {/* MT5 Status */}
            <div
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium transition-smooth ${mt5Connected
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'
                : 'bg-red-500/15 text-red-300 border border-red-500/20'
                }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${mt5Connected ? 'bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400/50' : 'bg-red-400'
                  }`}
              />
              <span className="hidden sm:inline font-medium">
                {mt5Connected ? t('mt5_connected') : t('mt5_disconnected')}
              </span>
              <span className="sm:hidden font-medium">MT5</span>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Time */}
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-300 font-medium">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="font-mono">{currentTime.toLocaleTimeString()}</span>
            </div>

            {/* Refresh button */}
            <button
              className="hidden sm:block p-2.5 hover:bg-slate-800/60 rounded-lg transition-smooth active:scale-95 group"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-5 h-5 text-slate-400 group-hover:text-slate-300 group-hover:rotate-180 transition-all duration-500" />
            </button>

            {/* Settings button */}
            <button className="hidden sm:block p-2.5 hover:bg-slate-800/60 rounded-lg transition-smooth active:scale-95 group">
              <Settings className="w-5 h-5 text-slate-400 group-hover:text-slate-300 group-hover:rotate-90 transition-all duration-300" />
            </button>

            {/* Notification Center - Solo su desktop */}
            <div className="hidden lg:block">
              <NotificationCenter />
            </div>

            {/* User Profile Custom Button */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => {
                  useAppStore.getState().setActiveTab('profile');
                }}
                className="relative group transition-smooth active:scale-95"
              >
                <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-slate-700/60 group-hover:border-violet-500/60 transition-smooth shadow-soft group-hover:shadow-medium">
                  <img
                    src={useUser().user?.imageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500/0 to-cyan-500/0 group-hover:from-violet-500/10 group-hover:to-cyan-500/10 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
